import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Grid, List, Eye, EyeOff, DollarSign, Filter, X, Package2, Star, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ProductAddonsManagement = () => {
  const { user, restaurants: userRestaurants } = useAuth();
  const [addons, setAddons] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    isActive: '',
    priceRange: '',
    addonType: ''
  });
  const [formData, setFormData] = useState({
    restaurantId: '',
    productId: '',
    name: '',
    description: '',
    price: '',
    isActive: true
  });
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // API Services
  const addonService = {
    getAll: async () => {
      if (!user) return [];
      
      console.log('Calling product addons API...');
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/productaddons`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Product addons data:', data);
        return data;
      } catch (error) {
        console.error('Product addons API error:', error);
        throw error;
      }
    },
    create: async (data) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/productaddons`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    },
    update: async (id, data) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/productaddons/${id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(data)
      });
       

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

        const text = await response.text(); 
 

        try {
            const parsed = JSON.parse(text);
            console.log('✅ Parsed JSON:', parsed);
            return parsed;
        } catch (e) {
            console.error('❌ JSON parse failed:', e);
            console.error('❌ Text was:', text);
            throw new Error('Invalid JSON response');
        }
      return response.json();
    },
    delete: async (id) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/productaddons/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        mode: 'cors'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.ok;
    }
  };

  const restaurantService = {
    getAll: async () => {
      if (!user) return [];
      
      try {
        const token = localStorage.getItem('authToken');
        let url = `${API_BASE_URL}/api/Restaurants`;
        
        // Convert numeric role to string for comparison
        const userRole = typeof user.role === 'number' ? 
          (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
          user.role;
        
        // If user is not SuperAdmin, filter by their restaurant
        if (userRole !== 'SuperAdmin') {
          url = `${API_BASE_URL}/api/Restaurants/user/${user.id}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          return data;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Restaurant API Error:', error);
        throw error;
      }
    }
  };

  // Load Data
  const fetchData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      console.log('🔄 Fetching all data...');
      
      // Use userRestaurants if available, otherwise fetch from API
      let restaurantsData;
      if (userRestaurants && userRestaurants.length > 0) {
        restaurantsData = userRestaurants;
        setRestaurants(restaurantsData);
      } else {
        restaurantsData = await restaurantService.getAll();
        setRestaurants(restaurantsData);
      }
      
      const addonsData = await addonService.getAll();
      
      console.log('✅ All data loaded successfully');
      setAddons(addonsData);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      setAddons([]);
      setRestaurants([]);
      alert(`API Hatası: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createAddon = async () => {
    try {
      // ProductId varsa GUID olarak gönder, yoksa gönderme
      const requestData = {
        restaurantId: formData.restaurantId,
        name: formData.name,
        description: formData.description || '',
        price: parseFloat(formData.price) || 0,
        isActive: formData.isActive
      };
      
      // ProductId seçilmişse ekle
      if (formData.productId && formData.productId.trim() !== '') {
        requestData.productId = formData.productId;
      }
      
      await addonService.create(requestData);
      await fetchData();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error creating addon:', error);
      alert('Eklenti oluşturulurken bir hata oluştu!');
    }
  };

    const updateAddon = async () => {
        if (!editingAddon) return;

        try {
            // ProductId varsa GUID olarak gönder, yoksa gönderme
            const requestData = {
                restaurantId: formData.restaurantId,
                name: formData.name,
                description: formData.description || '',
                price: parseFloat(formData.price) || 0,
                isActive: formData.isActive
            };
            
            // ProductId seçilmişse ekle
            if (formData.productId && formData.productId.trim() !== '') {
                requestData.productId = formData.productId;
            }
            
            // ✅ API'den güncellenmiş DTO gelecek
            const updatedAddonDto = await addonService.update(editingAddon.id, requestData);

            // ✅ State'i güncellenmiş data ile direkt güncelle (fetchData'ya gerek yok)
            setAddons(prevAddons =>
                prevAddons.map(addon =>
                    addon.id === editingAddon.id ? updatedAddonDto : addon
                )
            );

            // Form'u temizle ve modal'ı kapat
            resetForm();
            setShowModal(false);
            setEditingAddon(null);

            console.log('✅ Addon updated successfully:', updatedAddonDto);
        } catch (error) {
            console.error('Error updating addon:', error);
            alert('Eklenti güncellenirken bir hata oluştu!');
        }
    };

  const deleteAddon = async (id) => {
    if (window.confirm('Bu eklentiyi silmek istediğinizden emin misiniz?')) {
      try {
        await addonService.delete(id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting addon:', error);
        alert('Eklenti silinirken bir hata oluştu!');
      }
    }
  };

  const toggleAddonStatus = async (addon) => {
    try {
      const updatedData = { ...addon, isActive: !addon.isActive };
      await addonService.update(addon.addonId, updatedData);
      await fetchData();
    } catch (error) {
      console.error('Error toggling addon status:', error);
      alert('Eklenti durumu değiştirilirken bir hata oluştu!');
    }
  };

  const resetForm = () => {
    setFormData({
      restaurantId: '',
      productId: '',
      name: '',
      description: '',
      price: '',
      isActive: true
    });
    setProducts([]);
  };

  // Restorana göre ürünleri getir
  const fetchProductsByRestaurant = async (restaurantId) => {
    if (!restaurantId) {
      setProducts([]);
      return;
    }

    setProductsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const userRole = typeof user?.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user?.role;
      
      let url = `${API_BASE_URL}/api/Products`;
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Products/user/${user.id}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const allProducts = await response.json();
        // Seçilen restorana göre filtrele
        const filteredProducts = allProducts.filter(product => {
          const productRestaurantId = product.restaurantId || product.RestaurantId || product.restaurant_id;
          return productRestaurantId === restaurantId;
        });
        setProducts(filteredProducts);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const openModal = (addon) => {
    if (addon) {
      setEditingAddon(addon);
      setFormData({
        restaurantId: addon.restaurantId,
        productId: addon.productId || '',
        name: addon.name,
        description: addon.description || '',
        price: addon.price.toString(),
        isActive: addon.isActive
      });
      // Ürünleri yükle
      if (addon.restaurantId) {
        fetchProductsByRestaurant(addon.restaurantId);
      }
    } else {
      setEditingAddon(null);
      resetForm();
    }
    setShowModal(true);
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find(r => r.id === restaurantId);
    return restaurant ? restaurant.name : 'Bilinmeyen Restoran';
  };

  const getAddonIcon = (name) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('peynir') || nameLower.includes('cheese')) return '🧀';
    if (nameLower.includes('sos') || nameLower.includes('sauce')) return '🥫';
    if (nameLower.includes('et') || nameLower.includes('meat')) return '🥩';
    if (nameLower.includes('sebze') || nameLower.includes('vegetable')) return '🥬';
    if (nameLower.includes('mantar') || nameLower.includes('mushroom')) return '🍄';
    if (nameLower.includes('zeytin') || nameLower.includes('olive')) return '🫒';
    if (nameLower.includes('domates') || nameLower.includes('tomato')) return '🍅';
    if (nameLower.includes('soğan') || nameLower.includes('onion')) return '🧅';
    if (nameLower.includes('biber') || nameLower.includes('pepper')) return '🌶️';
    if (nameLower.includes('krema') || nameLower.includes('cream')) return '🥛';
    if (nameLower.includes('çikolata') || nameLower.includes('chocolate')) return '🍫';
    if (nameLower.includes('meyve') || nameLower.includes('fruit')) return '🍓';
    return '🍴';
  };

  const getAddonType = (name) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('sos') || nameLower.includes('sauce')) return 'Sos';
    if (nameLower.includes('peynir') || nameLower.includes('cheese')) return 'Peynir';
    if (nameLower.includes('et') || nameLower.includes('meat')) return 'Et';
    if (nameLower.includes('sebze') || nameLower.includes('vegetable')) return 'Sebze';
    if (nameLower.includes('baharat') || nameLower.includes('spice')) return 'Baharat';
    if (nameLower.includes('tatlı') || nameLower.includes('sweet')) return 'Tatlı';
    return 'Diğer';
  };

  const getPriceCategory = (price) => {
    const priceNum = parseFloat(price);
    if (priceNum === 0) return { label: 'Ücretsiz', color: 'bg-green-100 text-green-800' };
    if (priceNum <= 5) return { label: 'Ekonomik', color: 'bg-blue-100 text-blue-800' };
    if (priceNum <= 15) return { label: 'Orta', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Premium', color: 'bg-purple-100 text-purple-800' };
  };

  const clearFilters = () => {
    setFilters({
      isActive: '',
      priceRange: '',
      addonType: ''
    });
    setSelectedRestaurant('');
    setSearchTerm('');
  };

  const filteredAddons = addons.filter(addon => {
    // Text search
    const matchesSearch = addon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (addon.description && addon.description.toLowerCase().includes(searchTerm.toLowerCase()));

    // Restaurant filter
    const matchesRestaurant = !selectedRestaurant || addon.restaurantId === selectedRestaurant;

    // Advanced filters
    const matchesActive = filters.isActive === '' || 
      (filters.isActive === 'true' && addon.isActive) ||
      (filters.isActive === 'false' && !addon.isActive);

    const matchesPriceRange = () => {
      if (!filters.priceRange) return true;
      const price = parseFloat(addon.price);
      if (filters.priceRange === 'free') return price === 0;
      if (filters.priceRange === 'low') return price > 0 && price <= 5;
      if (filters.priceRange === 'medium') return price > 5 && price <= 15;
      if (filters.priceRange === 'high') return price > 15;
      return true;
    };

    const matchesType = !filters.addonType || getAddonType(addon.name) === filters.addonType;

    return matchesSearch && matchesRestaurant && matchesActive && matchesPriceRange() && matchesType;
  });

  const addonTypes = [...new Set(addons.map(addon => getAddonType(addon.name)))];

  useEffect(() => {
    if (userRestaurants && userRestaurants.length > 0) {
      setRestaurants(userRestaurants);
      
      // Eğer sadece 1 restaurant varsa, onu otomatik seç
      if (userRestaurants.length === 1) {
        const restaurantId = userRestaurants[0].id || userRestaurants[0].restaurantId;
        setFormData(prev => ({
          ...prev,
          restaurantId: restaurantId
        }));
      }
    }
    fetchData();
  }, [userRestaurants]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Ürün Eklentileri Yönetimi</h1>
          <p className="text-gray-600">Ekstra malzemeler, soslar ve eklentileri yönetin</p>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Eklenti ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Restaurant Filter */}
              <select
                value={selectedRestaurant}
                onChange={(e) => setSelectedRestaurant(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tüm Restoranlar</option>
                {restaurants.map(restaurant => (
                  <option key={restaurant.id} value={restaurant.id}>
                    {restaurant.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              {/* Advanced Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 border border-gray-300 rounded-lg ${showFilters ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                title="Advanced Filters"
              >
                <Filter className="w-4 h-4" />
              </button>

              {/* Clear Filters */}
              <button
                onClick={clearFilters}
                className="p-2 border border-gray-300 rounded-lg bg-white text-gray-600 hover:bg-gray-50"
                title="Clear Filters"
              >
                <X className="w-4 h-4" />
              </button>

              {/* View Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {/* Add Button */}
              <button
                onClick={() => openModal()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Yeni Eklenti
              </button>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Durum</label>
                  <select
                    value={filters.isActive}
                    onChange={(e) => setFilters({...filters, isActive: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tümü</option>
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>

                {/* Price Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fiyat Aralığı</label>
                  <select
                    value={filters.priceRange}
                    onChange={(e) => setFilters({...filters, priceRange: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tümü</option>
                    <option value="free">Ücretsiz (0₺)</option>
                    <option value="low">Ekonomik (0₺-5₺)</option>
                    <option value="medium">Orta (5₺-15₺)</option>
                    <option value="high">Premium (15₺+)</option>
                  </select>
                </div>

                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Eklenti Türü</label>
                  <select
                    value={filters.addonType}
                    onChange={(e) => setFilters({...filters, addonType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tümü</option>
                    {addonTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{addons.length}</div>
                  <div className="text-sm text-gray-500">Toplam Eklenti</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {addons.filter(a => a.isActive).length}
                  </div>
                  <div className="text-sm text-gray-500">Aktif Eklenti</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    ₺{addons.length > 0 ? (addons.reduce((sum, a) => sum + parseFloat(a.price), 0) / addons.length).toFixed(2) : '0.00'}
                  </div>
                  <div className="text-sm text-gray-500">Ortalama Fiyat</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Star className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {addons.filter(a => parseFloat(a.price) === 0).length}
                  </div>
                  <div className="text-sm text-gray-500">Ücretsiz Eklenti</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Addons Grid/List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filteredAddons.length === 0 ? (
          <div className="text-center py-12">
            <Package2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Eklenti bulunamadı</h3>
            <p className="text-gray-500 mb-4">Arama kriterlerinize uygun eklenti bulunmuyor.</p>
            <button
              onClick={() => openModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              İlk eklentiyi oluştur
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAddons.map((addon) => (
              <div key={addon.addonId} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="text-3xl">{getAddonIcon(addon.name)}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{addon.name}</h3>
                        <p className="text-sm text-gray-500">{getRestaurantName(addon.restaurantId)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleAddonStatus(addon)}
                      className={`p-1.5 rounded-full ${addon.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                      title={addon.isActive ? 'Aktif' : 'Pasif'}
                    >
                      {addon.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </div>

                  {addon.description && (
                    <p className="text-gray-600 text-sm mb-4">{addon.description}</p>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                      {getAddonType(addon.name)}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${getPriceCategory(addon.price).color}`}>
                      {getPriceCategory(addon.price).label}
                    </span>
                  </div>

                  {/* Price and Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-lg font-bold text-gray-900">
                        {parseFloat(addon.price) === 0 ? 'Ücretsiz' : `₺${addon.price}`}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openModal(addon)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Düzenle"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteAddon(addon.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
                <div className="col-span-3">Eklenti</div>
                <div className="col-span-2">Restoran</div>
                <div className="col-span-2">Tür</div>
                <div className="col-span-2">Fiyat</div>
                <div className="col-span-2">Durum</div>
                <div className="col-span-1">İşlemler</div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredAddons.map((addon) => (
                <div key={addon.addonId} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getAddonIcon(addon.name)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{addon.name}</div>
                          {addon.description && (
                            <div className="text-xs text-gray-500 truncate">{addon.description}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-600">{getRestaurantName(addon.restaurantId)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                        {getAddonType(addon.name)}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {parseFloat(addon.price) === 0 ? 'Ücretsiz' : `₺${addon.price}`}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getPriceCategory(addon.price).color}`}>
                          {getPriceCategory(addon.price).label}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        addon.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {addon.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openModal(addon)}
                          title="Düzenle"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteAddon(addon.addonId)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingAddon ? 'Eklenti Düzenle' : 'Yeni Eklenti Ekle'}
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Restaurant Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Restoran *
                  </label>
                  <select
                    value={formData.restaurantId}
                    onChange={(e) => {
                      const newRestaurantId = e.target.value;
                      setFormData({...formData, restaurantId: newRestaurantId, productId: ''});
                      // Restoran değiştiğinde ürünleri yükle
                      fetchProductsByRestaurant(newRestaurantId);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Restoran Seçin</option>
                    {restaurants.map(restaurant => (
                      <option key={restaurant.id} value={restaurant.id}>
                        {restaurant.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Product Selection (Optional) */}
                {formData.restaurantId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ürün (Opsiyonel)
                    </label>
                    <select
                      value={formData.productId}
                      onChange={(e) => setFormData({...formData, productId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={productsLoading}
                    >
                      <option value="">Ürün Seçin (Opsiyonel)</option>
                      {productsLoading ? (
                        <option value="" disabled>Yükleniyor...</option>
                      ) : (
                        products.map(product => {
                          const productId = product.id || product.productId || product.ProductId || product.ID;
                          const productName = product.name || product.Name || '';
                          return (
                            <option key={productId} value={productId}>
                              {productName}
                            </option>
                          );
                        })
                      )}
                    </select>
                    {products.length === 0 && !productsLoading && formData.restaurantId && (
                      <p className="mt-1 text-sm text-gray-500">Bu restoran için ürün bulunamadı</p>
                    )}
                  </div>
                )}

                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Eklenti Adı *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Extra Peynir, Acı Sos..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fiyat (₺) *
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Eklenti açıklaması..."
                  />
                </div>

                {/* Preview */}
                {formData.name && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Eklenti Önizlemesi</h4>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getAddonIcon(formData.name)}</span>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-blue-900">{formData.name}</div>
                        {formData.description && (
                          <div className="text-xs text-blue-700">{formData.description}</div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                            {getAddonType(formData.name)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${getPriceCategory(formData.price).color}`}>
                            {getPriceCategory(formData.price).label}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-blue-900">
                        {parseFloat(formData.price || 0) === 0 ? 'Ücretsiz' : `₺${formData.price}`}
                      </div>
                    </div>
                  </div>
                )}

                {/* Status */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Aktif</span>
                  </label>
                </div>

                {/* Common Addon Examples */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Popüler Eklenti Örnekleri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <strong>Pizza Eklentileri:</strong>
                      <ul className="mt-1 space-y-1">
                        <li>• Extra Peynir (+8₺)</li>
                        <li>• Mantar (+5₺)</li>
                        <li>• Sucuk (+10₺)</li>
                        <li>• Zeytin (+3₺)</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Burger Eklentileri:</strong>
                      <ul className="mt-1 space-y-1">
                        <li>• Avokado (+6₺)</li>
                        <li>• Bacon (+8₺)</li>
                        <li>• Acı Sos (Ücretsiz)</li>
                        <li>• Extra Köfte (+12₺)</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Kahve Eklentileri:</strong>
                      <ul className="mt-1 space-y-1">
                        <li>• Extra Shot (+4₺)</li>
                        <li>• Soya Sütü (+2₺)</li>
                        <li>• Şurup (+3₺)</li>
                        <li>• Krema (Ücretsiz)</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Salata Eklentileri:</strong>
                      <ul className="mt-1 space-y-1">
                        <li>• Tavuk (+15₺)</li>
                        <li>• Avokado (+8₺)</li>
                        <li>• Feta Peyniri (+6₺)</li>
                        <li>• Ceviz (+4₺)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Pricing Guidelines */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2">💡 Fiyatlandırma Önerileri</h4>
                  <div className="text-sm text-yellow-800 space-y-1">
                    <div><strong>Ücretsiz (0₺):</strong> Basit soslar, baharatlar</div>
                    <div><strong>Ekonomik (1₺-5₺):</strong> Sebze eklentileri, basit malzemeler</div>
                    <div><strong>Orta (5₺-15₺):</strong> Peynir, et ürünleri, özel soslar</div>
                    <div><strong>Premium (15₺+):</strong> Avokado, truffle, özel et çeşitleri</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingAddon(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={editingAddon ? updateAddon : createAddon}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                {editingAddon ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAddonsManagement;